import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Base_URL, ApiCall } from "../Common/Path";
import customFailAlert from "../Alerts/customFailAlert";

const ChangeOwnerModal = ({
  open,
  onClose,
  household,
  onOwnerChanged,
}) => {
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [isChanging, setIsChanging] = useState(false);

  // Get current owner and all members
  const members = Array.isArray(household?.members) ? household.members : [];
  const currentOwner = members.find((member) => member?.isOwner);
  const currentOwnerId = currentOwner?.id || "";

  // Initialize with current owner when modal opens
  useEffect(() => {
    if (open && household) {
      setSelectedMemberId(currentOwnerId);
    }
  }, [open, household, currentOwnerId]);

  const handleClose = () => {
    setSelectedMemberId("");
    setIsChanging(false);
    onClose();
  };

  const handleChange = async () => {
    if (!household || !selectedMemberId || selectedMemberId === currentOwnerId || isChanging) return;

    // Validate that selected member has accepted the invitation
    const selectedMember = members.find((m) => m.id === selectedMemberId);
    if (!selectedMember || !isMemberAccepted(selectedMember)) {
      customFailAlert("New owner must have accepted the household invitation");
      return;
    }

    try {
      setIsChanging(true);

      // Step 1: Change the owner
      const body = {
        householdMemberId: selectedMemberId,
      };

      const url = `${Base_URL}/households/${household.id}/owner`;
      const response = await ApiCall(url, body, "put");
      const statusCode = response?.statusCode || response?.code;

      const ok =
        statusCode === 200 ||
        statusCode === 201 ||
        String(response?.status || "").toUpperCase() === "SUCCESS";

      if (!ok) {
        const errorMessage =
          response?.message ||
          "Unable to change household owner. Please try again.";
        customFailAlert(errorMessage);
        if (onOwnerChanged) {
          onOwnerChanged({ success: false, message: errorMessage });
        }
        return;
      }

      // Step 2: Automatically remove the old owner
      if (currentOwner && currentOwner.id) {
        try {
          const removeBody = {
            householdMemberId: currentOwner.id,
            reason: "Removed automatically after ownership change",
          };

          const removeUrl = `${Base_URL}/households/${household.id}/members`;
          const removeResponse = await ApiCall(removeUrl, removeBody, "delete");
          const removeStatusCode = removeResponse?.statusCode || removeResponse?.code;

          const removeOk =
            removeStatusCode === 200 ||
            removeStatusCode === 204 ||
            removeStatusCode === 201 ||
            String(removeResponse?.status || "").toUpperCase() === "SUCCESS";

          if (!removeOk) {
            // Owner changed successfully but removal failed - still report success for owner change
            console.warn("Owner changed but failed to remove old owner:", removeResponse?.message);
            if (onOwnerChanged) {
              onOwnerChanged({
                success: true,
                message: "Household owner changed successfully. However, failed to remove the previous owner.",
                data: response,
              });
            }
          } else {
            // Both operations succeeded
            if (onOwnerChanged) {
              onOwnerChanged({
                success: true,
                message: "Household owner changed and previous owner removed successfully",
                data: response,
              });
            }
          }
        } catch (removeError) {
          // Owner changed successfully but removal failed - still report success for owner change
          console.error("Error removing old owner:", removeError);
          if (onOwnerChanged) {
            onOwnerChanged({
              success: true,
              message: "Household owner changed successfully. However, failed to remove the previous owner.",
              data: response,
            });
          }
        }
      } else {
        // No old owner to remove (edge case)
        if (onOwnerChanged) {
          onOwnerChanged({
            success: true,
            message: "Household owner changed successfully",
            data: response,
          });
        }
      }

      handleClose();
    } catch (error) {
      console.error("Error changing household owner:", error);
      const errorMessage = "Something went wrong while changing the owner.";
      customFailAlert(errorMessage);
      if (onOwnerChanged) {
        onOwnerChanged({ success: false, message: errorMessage });
      }
    } finally {
      setIsChanging(false);
    }
  };

  // Get member display name
  const getMemberDisplayName = (member) => {
    const info = member?.userInfo || {};
    const name = info.name || 
      `${info.firstName || ""} ${info.lastName || ""}`.trim() ||
      member?.userRole?.user?.email ||
      "Unknown Member";
    return name;
  };

  // Check if member has accepted the invitation
  const isMemberAccepted = (member) => {
    const status = String(member?.status || "").toUpperCase();
    return status === "ACCEPTED";
  };

  // Get selected member object
  const selectedMember = members.find((m) => m.id === selectedMemberId);
  const isSelectedMemberAccepted = selectedMember ? isMemberAccepted(selectedMember) : false;

  const isOwnerChanged = selectedMemberId && selectedMemberId !== currentOwnerId;
  const canChange = isOwnerChanged && isSelectedMemberAccepted && !isChanging;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#000", mb: 0.5 }}
            >
              Change Household Owner
            </Typography>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              Select another owner for the house from the member list. The previous owner will be automatically removed from the household.
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: "#6B7280", mt: -1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        {currentOwner && (
          <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
            <Typography variant="body2" sx={{ fontSize: "13px" }}>
              The current owner ({getMemberDisplayName(currentOwner)}) will be automatically removed from the household after the ownership change.
            </Typography>
          </Alert>
        )}
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel
            id="household-owner-label"
            sx={{
              "&.Mui-focused": {
                color: "#2563EB",
              },
            }}
          >
            Household Owner
          </InputLabel>
          <Select
            labelId="household-owner-label"
            id="household-owner-select"
            value={selectedMemberId}
            onChange={(e) => setSelectedMemberId(e.target.value)}
            label="Household Owner"
            disabled={isChanging}
            sx={{
              borderRadius: "4px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2563EB",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2563EB",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2563EB",
              },
            }}
          >
            {members.map((member) => {
              const displayName = getMemberDisplayName(member);
              const isAccepted = isMemberAccepted(member);
              const isCurrentOwner = member.id === currentOwnerId;
              
              return (
                <MenuItem 
                  key={member.id} 
                  value={member.id}
                  disabled={!isAccepted && !isCurrentOwner}
                  sx={{
                    opacity: (!isAccepted && !isCurrentOwner) ? 0.5 : 1,
                  }}
                >
                  {displayName}
                  {!isAccepted && !isCurrentOwner && (
                    <Typography
                      component="span"
                      sx={{
                        ml: 1,
                        fontSize: "12px",
                        color: "#9CA3AF",
                        fontStyle: "italic",
                      }}
                    >
                      (Pending Invite)
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        
        {selectedMemberId && selectedMemberId !== currentOwnerId && !isSelectedMemberAccepted && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: "4px",
              backgroundColor: "#FEF3C7",
              border: "1px solid #FCD34D",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "#B45309",
                fontWeight: 500,
              }}
            >
              ⚠️ New owner must have accepted the household invitation
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isChanging}
          sx={{
            textTransform: "none",
            px: 4,
            py: 1.1,
            borderRadius: "4px",
            borderColor: "#D1D5DB",
            color: "#374151",
            fontWeight: 600,
            "&:hover": {
              borderColor: "#9CA3AF",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleChange}
          variant="contained"
          disabled={!canChange}
          sx={{
            textTransform: "none",
            px: 4,
            py: 1.1,
            borderRadius: "4px",
            backgroundColor: canChange ? "#2563EB" : "#E5E7EB",
            color: canChange ? "#FFFFFF" : "#9CA3AF",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: canChange ? "#1D4ED8" : "#E5E7EB",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {isChanging ? "Changing..." : "Change Owner"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeOwnerModal;

